import React, { useEffect, useState, useContext, useMemo } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, Container, Row, Col, Button } from 'react-bootstrap';
import axios from 'axios';
import { toast } from 'react-toastify';

import { RoleContext } from '../../RoleContext';
import AssignSpecialtiesModal from './AssignSpecialtiesModal';
import TableSmall from '../../Theme/TableSmall';

/* ------------------------------------------------------------------ */
/*                         MAIN COMPONENT                             */
/* ------------------------------------------------------------------ */
const HospitalDetail = () => {
  const roleData = useContext(RoleContext);
  const role = JSON.parse(roleData);

  const { hospitalId } = useParams();

  const [hospital, setHospital]     = useState(null);
  const [specialties, setSpecialties] = useState([]);
  const [loading, setLoading]       = useState(true);
  const [showAssignModal, setShowAssignModal] = useState(false);

  /* ----------------------- API helpers ----------------------- */
  const fetchHospital = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`/hospitals/${hospitalId}/specialties`);
      setHospital(data);
      setSpecialties(data.specialties || []);
    } catch (err) {
      console.error('Failed to fetch hospital details', err);
      toast.error('Could not load hospital');
      setHospital(null);
      setSpecialties([]);
    } finally {
      setLoading(false);
    }
  };

  const deleteSpeciality = async (specId) => {
    try {
      await axios.post('/remove_speciality', {
        hospitalId: hospitalId,
        specialtyIds: [specId],
      });
      toast.success('Deleted successfully');
      fetchHospital();
    } catch (err) {
      console.error('Failed to delete specialty', err);
      toast.error('Failed to delete specialty');
    }
  };

  /* ----------------------- lifecycle ------------------------- */
  useEffect(() => {
    fetchHospital();
  }, [hospitalId]);

  /* -------------------- table definition --------------------- */
  const specialityColumns = useMemo(
    () => [
      {
        Header: 'Speciality Lookup',
        columns: [
          { Header: 'Name', accessor: 'name', disableFilters: true },
        ],
      },
      {
        Header: ' ',
        columns: [
          {
            Header: 'Actions',
            id: 'actions',   // needed if accessor-less
            disableFilters: true,
            Cell: ({ row }) =>
              role.some((r) => r.perm_name === 'remove_specialty_from_hospital') && (
                <Link
                  to="#"
                  onClick={() =>
                    deleteSpeciality(row.original.specialty_id)
                  }
                >
                  <i className="fas fa-trash error" />
                </Link>
              ),
          },
        ],
      },
    ],
    [role],
  );

  /* ------------------------ guards --------------------------- */
  if (loading)      return <div className="text-center my-4">Loading…</div>;
  if (!hospital)    return <div className="text-center my-4">Hospital details not found.</div>;

  /* ------------------------ UI ------------------------------- */
  return (
    <div className="content-wrappers" style={{ paddingTop: 20 }}>
      {/* ------------ header ------------ */}
      <section className="content-header">
        <Container fluid>
          <Row className="mb-2">
            <Col sm={6}>
              <h1>Hospital Details</h1>
            </Col>
            <Col sm={6}>
              <ol className="breadcrumb float-sm-right">
                <li className="breadcrumb-item">
                  <Link to="/ambulance_lookup">Home</Link>
                </li>
                <li className="breadcrumb-item active">Hospital Detail</li>
              </ol>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ------------ main content ------------ */}
      <section className="content">
        <Container fluid>
          <Row className="mt-3">
            {/* -------- left: hospital info -------- */}
            <Col md={4}>
              <Card>
                <Card.Header>
                  <h3 className="card-title">Hospital Details</h3>
                </Card.Header>
                <Card.Body>
                  <p><b>Hospital Name:</b> {hospital.name}</p>
                  <p><b>Address:</b> {hospital.location || 'N/A'}</p>
                  <p><b>Phone Number:</b> {hospital.phoneNumber || 'N/A'}</p>
                  <p><b>Latitude:</b> {hospital.latitude}</p>
                  <p><b>Longitude:</b> {hospital.longitude}</p>
                  <p><b>Hospitals Email:</b> {hospital.hospitalsEmail}</p>
                  <p><b>Branch Name:</b> {hospital.branchName}</p>
                  <p><b>Subcity:</b> {hospital.subCity}</p>
                  <p><b>Woreda:</b> {hospital.woreda}</p>
                  <p><b>Classification of Hospitals:</b> {hospital.classificationofHospitals}</p>
                </Card.Body>
              </Card>
            </Col>

            {/* -------- right: specialties -------- */}
            <Col md={8}>
              <Card>
                <Card.Header className="d-flex justify-content-between align-items-center">
                  <h3 className="card-title">Specialties</h3>
                  {role.some((r) => r.perm_name === 'assign_specialty_to_hospital') && (
                    <Button
                      className="btn btn-success"
                      onClick={() => setShowAssignModal(true)}
                    >
                      Assign Specialty
                    </Button>
                  )}
                </Card.Header>
                <Card.Body>
                  <TableSmall columns={specialityColumns} data={specialties} />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ------------ modal ------------ */}
      <AssignSpecialtiesModal
        showModal={showAssignModal}
        hideModal={() => setShowAssignModal(false)}
        hospitalId={hospitalId}
        assignedSpecialties={specialties}
        onSuccess={fetchHospital}
      />
    </div>
  );
};

export default HospitalDetail;
